import { useState, useEffect } from 'react';

/**
 * useIsMobile - Détecte si le viewport est en dessous d'un breakpoint.
 *
 * Remplace les `window.innerWidth` dispersés dans les composants, dont certains
 * ne réagissaient pas au redimensionnement ni à la rotation de l'écran.
 *
 * Utilise matchMedia plutôt qu'un listener resize : l'événement n'est émis que
 * lors du franchissement du seuil, et non à chaque pixel parcouru.
 *
 * @param {number} breakpoint - Largeur seuil en px (défaut: 640, breakpoint `sm` de Tailwind)
 * @returns {boolean} true si le viewport est strictement en dessous du breakpoint
 *
 * @example
 * const isMobile = useIsMobile();        // < 640px (téléphone)
 * const isTablet = useIsMobile(1024);    // < 1024px (téléphone + tablette)
 */
export const useIsMobile = (breakpoint = 640) => {
    const query = `(max-width: ${breakpoint - 1}px)`;

    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia(query).matches;
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const mediaQuery = window.matchMedia(query);
        const handleChange = (event) => setIsMobile(event.matches);

        // Resynchronise si le breakpoint change entre deux rendus
        setIsMobile(mediaQuery.matches);
        mediaQuery.addEventListener('change', handleChange);

        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [query]);

    return isMobile;
};

export default useIsMobile;
